import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["page", "itemsPerPage", "pageSizes", "noItemsPerPageMenu", "disabled", "totalRows", "onPagination", "container", "paginatorCustomStrings", "aria-label", "navigationTooltipPosition", "tableComponent", "paginatorClassName", "noHorizontalPadding"],
      _excluded2 = ["paginator"],
      _excluded3 = ["customStrings", "className"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { PAGINATOR_PAGE_SIZES } from "../../common/constants";
import { deprecated } from "../../common/proptypes";
import Paginator from "../../Paginator/Paginator";
/**
 * `withPaginator` is an HOC that adds [Paginator](#paginator) to [Table](#table) for
 * navigating through data set.
 * @type {Component}
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const TablePaginator = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    page,
    itemsPerPage,
    pageSizes,
    noItemsPerPageMenu,
    disabled,
    totalRows,
    onPagination,
    container,
    paginatorCustomStrings,
    "aria-label": ariaLabel,
    navigationTooltipPosition,
    tableComponent: TableComponent,
    paginatorClassName,
    noHorizontalPadding
  } = props,
        tableProps = _objectWithoutProperties(props, _excluded);

  const internalRef = useRef();
  const tableRef = ref || internalRef;
  const paginatorContainerNode = useRef(document.createElement("div"));
  useEffect(() => {
    let paginatorRootNode = "";
    const containerNode = paginatorContainerNode.current;

    if (container) {
      paginatorRootNode = typeof container === "string" ? document.querySelector(container) : container.current;
      paginatorRootNode.appendChild(containerNode);
    }
    /*
     * Do not remove the container node if it was passed as a ref;
     * the node's rendering is handled by another React Element.
     */


    return () => {
      if (container && typeof container === "string" && paginatorRootNode) {
        paginatorRootNode.removeChild(containerNode);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container]);
  return _jsxs(React.Fragment, {
    children: [container ? /*#__PURE__*/ReactDOM.createPortal(_jsx(Paginator, {
      className: paginatorClassName,
      page: page,
      itemsPerPage: itemsPerPage,
      pageSizes: pageSizes,
      noItemsPerPageMenu: noItemsPerPageMenu,
      disabled: disabled,
      totalItems: totalRows,
      "aria-label": ariaLabel,
      onPagination: onPagination,
      customStrings: paginatorCustomStrings,
      navigationTooltipPosition: navigationTooltipPosition,
      noHorizontalPadding: noHorizontalPadding
    }), paginatorContainerNode.current) : _jsx(Paginator, {
      className: paginatorClassName,
      page: page,
      itemsPerPage: itemsPerPage,
      pageSizes: pageSizes,
      noItemsPerPageMenu: noItemsPerPageMenu,
      disabled: disabled,
      totalItems: totalRows,
      "aria-label": ariaLabel,
      onPagination: onPagination,
      customStrings: paginatorCustomStrings,
      navigationTooltipPosition: navigationTooltipPosition
    }), _jsx(TableComponent, _objectSpread({
      ref: tableRef
    }, tableProps))]
  });
});
TablePaginator.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Callback that will be executed when user navigates across pages.
   * It receives the new page number and items per page as arguments.
   * @param {Number} page - Page number that is selected.
   * @param {Number} itemsPerPage - Number of items which need to be displayed in current page.
   */
  onPagination: PropTypes.func.isRequired,

  /**
   * The Table component to be extended
   */
  tableComponent: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types

  /** Aria label that will be applied to the Paginator container */
  "aria-label": PropTypes.string,

  /**
   * Container where Paginator component will be rendered.
   * This can be a selector string, or a [ref](https://reactjs.org/docs/refs-and-the-dom.html).
   */
  container: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })]),

  /** Disables the Paginator component if configured to true  */
  disabled: PropTypes.bool,

  /** Number of items to be displayed in a page. Only positive numbers are applicable */
  itemsPerPage: PropTypes.number,

  /**
   * Use this prop to set the position of the tooltips for the next
   * and previous buttons of the Paginator. This will be an object value
   * for independent positioning of the tooltips. The capability to accept
   * a single string value is maintained to not break the previous API.
   * If a string value is received, a default of previous: "top-right",
   * next: "bottom-right" will be applied.
   */
  navigationTooltipPosition: PropTypes.oneOfType([PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]), PropTypes.shape({
    previous: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"]),
    next: PropTypes.oneOf(["top-right", "top-center", "bottom-left", "bottom-center", "bottom-right", "left", "right"])
  })]),

  /** Does not render the itemsPerPageMenu in the accordion if configured to true */
  noItemsPerPageMenu: PropTypes.bool,

  /** Currently viewed page. Only positive numbers are applicable */
  page: PropTypes.number,

  /** Options which will be displayed in items per page menu. Maximum of 5 options can be passed  */
  pageSizes: PropTypes.arrayOf(PropTypes.number),

  /** Class name to Paginator root component. */
  paginatorClassName: PropTypes.string,

  /** Override for strings used within the component */
  paginatorCustomStrings: PropTypes.shape({
    /**
     * Label for the View Items title button.
     * Default to "View Items"
     */
    viewItemsTitle: PropTypes.string,

    /**
     * Aria label for the View Items title button.
     * Default to "Change item selection"
     */
    viewItemsTitleAriaLabel: PropTypes.string,

    /**
     * Label for the Items Per Page title button.
     * Default to "Items per page"
     */
    itemsPerPageTitle: PropTypes.string,

    /**
     * Aria label for the Items Per Page title button.
     * Default to "Change numbers of items per page"
     */
    itemsPerPageAriaLabel: PropTypes.string,

    /**
     * Label for the Previous button displayed in View Items Menu.
     * Default to "Previous"
     */
    previous: PropTypes.string,

    /**
     * DEPRECATED. Use `previousButtonDetails` instead.
     */
    previousButtonAriaLabel: deprecated(PropTypes.string, `Use "previousButtonDetails" instead`),

    /**
     * Hidden instructional text for the previous button in the View Items Menu.
     * Used for a11y purposes.
     *
     * Default to "Navigates to previous pages of items to choose from for display."
     */
    previousButtonDetails: PropTypes.string,

    /**
     * Label for the Next button displayed in View Items Menu.
     * Default to "Next"
     */
    next: PropTypes.string,

    /**
     * DEPRECATED. Use `nextButtonDetails` instead.
     */
    nextButtonAriaLabel: deprecated(PropTypes.string, `Use "nextButtonDetails" instead`),

    /**
     * Hidden instructional text for the next button in the View Items Menu.
     * Used for a11y purposes.
     *
     * Default to "Navigates to next pages of items to choose from for display."
     */
    nextButtonDetails: PropTypes.string,

    /**
     * Connector for button labels in Paginator.
     * Default to "to".
     * E.g.
     * `1 to 10` in View Items Menu.
     * `1 to 50 of 310 items` in trigger control
     */
    to: PropTypes.string,

    /**
     * Connector between `from` and `to` of buttons' aria label in Paginator.
     * Default to "through".
     * E.g.
     * `Items 1 through 50` in View Items Menu.
     * `1 through 50 of 310 items` in trigger control
     */
    through: PropTypes.string,

    /**
     * Connector for buttons' aria label in View Items menu.
     * Default to "Items".
     * E.g. `Items 1 through 50` in View Items Menu
     */
    items: PropTypes.string,

    /**
     * Connector for selected button's aria label in Paginator.
     * Default to "selected".
     * E.g.
     * `Items 1 through 50, selected` in View Items Menu.
     * `50 items per page, selected` in Items Per Page Menu
     */
    selected: PropTypes.string,

    /**
     * Connector for buttons' label and aria label in Items Per Page menu.
     * Default to "items per page".
     * E.g. `50 items per page` in Items Per Page Menu
     */
    itemsPerPage: PropTypes.string,

    /**
     * Connector for trigger control label and navigation buttons' aria label.
     * Default to "items".
     * E.g.
     * `1 to 50 of 310 items` in trigger control label.
     * `Previous 100 items` in Previous aria label.
     * `Next 100 items` in Next aria label
     */
    itemsInTrigger: PropTypes.string,

    /**
     * Connector for trigger control label and aria label.
     * Default to "of".
     * E.g. `1 to 100 of 310 items`
     */
    of: PropTypes.string,

    /**
     * Connector for Paginator selection status text.
     * It will be used for Accessibility purpose.
     * Default to "Viewing".
     * E.g. `Viewing 50 to 100 of 310 items` will be announced
     * on respective selection
     */
    viewing: PropTypes.string
  }),

  /**
   * Provides the total rows available across the data set
   */
  totalRows: PropTypes.number
} : {};
TablePaginator.defaultProps = {
  page: 1,
  itemsPerPage: 200,
  totalRows: null,
  pageSizes: PAGINATOR_PAGE_SIZES,
  noItemsPerPageMenu: false,
  disabled: false,
  "aria-label": null,
  container: null,
  paginatorClassName: null,
  paginatorCustomStrings: {},
  navigationTooltipPosition: {
    previous: "top-right",
    next: "bottom-right"
  }
};
/**
 * This is the actual HOC that will be exported. It returns a Table component
 * wrapped with the Paginator component defined above.
 */

function withPaginator(TableComponent) {
  const WrappedTable = /*#__PURE__*/React.forwardRef((props, ref) => {
    const {
      paginator
    } = props,
          others = _objectWithoutProperties(props, _excluded2);

    if (paginator) {
      const {
        customStrings,
        className: paginatorClassName
      } = paginator,
            paginatorProps = _objectWithoutProperties(paginator, _excluded3);

      return _jsx(TablePaginator, _objectSpread(_objectSpread(_objectSpread({}, others), {}, {
        paginatorClassName: paginatorClassName,
        paginatorCustomStrings: customStrings
      }, paginatorProps), {}, {
        ref: ref,
        tableComponent: TableComponent
      }));
    } else {
      return _jsx(TableComponent, _objectSpread(_objectSpread({}, others), {}, {
        ref: ref
      }));
    }
  });
  WrappedTable.displayName = `withPaginator(${TableComponent.displayName || TableComponent.name})`;
  return WrappedTable;
}

export default withPaginator;